import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  Menu,
  MenuItem as MenuItemComponent,
  Tabs,
  Tab,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Pagination,
} from '@mui/material';
import {
  NotificationsOutlined,
  Search,
  CheckCircle,
  NavigateNext,
  Factory,
  AttachMoney,
  LocationOn,
  CalendarToday,
  Description,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import logo from '../assets/logo.png';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const SchemesPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeTab, setActiveTab] = useState(3);
  const [schemes, setSchemes] = useState([]);
  const [filteredSchemes, setFilteredSchemes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    industryType: 'all',
    location: 'all',
    district: 'all'
  });
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'details'
  const [activeDetailTab, setActiveDetailTab] = useState(0); // 0: About, 1: Eligibility, 2: Benefits, 3: How to Apply, 4: Documents
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userInfo = localStorage.getItem('userInfo');
    
    if (!token) {
      navigate('/login');
      return;
    }
    
    if (userInfo) {
      setUser(JSON.parse(userInfo));
    }

    loadSchemes();
  }, [navigate]);

  const loadSchemes = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_URL}/schemes/`, {
        headers: { Authorization: `Token ${token}` }
      });
      const schemesData = response.data.results || response.data || [];
      setSchemes(schemesData);
      setFilteredSchemes(schemesData);
    } catch (error) {
      console.error('Error loading schemes:', error);
    }
  };

  const filterSchemes = useCallback(() => {
    let filtered = schemes;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(scheme =>
        scheme.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (scheme.short_description && scheme.short_description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Industry type filter
    if (filters.industryType !== 'all') {
      filtered = filtered.filter(scheme => 
        scheme.industry_type === filters.industryType || scheme.industry_type === 'all'
      );
    }

    // Location filter
    if (filters.location !== 'all') {
      filtered = filtered.filter(scheme => scheme.location === filters.location);
    }

    setFilteredSchemes(filtered);
  }, [schemes, searchQuery, filters]);

  useEffect(() => {
    filterSchemes();
  }, [searchQuery, filters, schemes, filterSchemes]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const applyFilters = () => {
    filterSchemes();
  };

  const handleViewDetails = (scheme) => {
    setSelectedScheme(scheme);
    setViewMode('details');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedScheme(null);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredSchemes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSchemes = filteredSchemes.slice(startIndex, endIndex);

  const handleRequestConsultation = () => {
    navigate('/messages');
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 0) navigate('/dashboard');
    if (newValue === 1) navigate('/projects');
    if (newValue === 2) navigate('/messages');
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userInfo');
    navigate('/login');
  };

  const capitalizeUsername = (username) => {
    if (!username) return '';
    return username.charAt(0).toUpperCase() + username.slice(1);
  };

  const capitalizeWords = (str) => {
    if (!str) return '';
    return str.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  // Render Schemes List View
  const renderSchemesList = () => (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#333' }}>
        Schemes & Subsidies
      </Typography>

      {/* Filters Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search schemes by name or keyword..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Industry Type</InputLabel>
              <Select
                value={filters.industryType}
                label="Industry Type"
                onChange={(e) => handleFilterChange('industryType', e.target.value)}
              >
                <MenuItem value="all">All Industries</MenuItem>
                <MenuItem value="food_processing">Food Processing</MenuItem>
                <MenuItem value="textile">Textile</MenuItem>
                <MenuItem value="engineering">Engineering</MenuItem>
                <MenuItem value="electronics">Electronics</MenuItem>
                <MenuItem value="renewable_energy">Renewable Energy</MenuItem>
                <MenuItem value="manufacturing">Manufacturing</MenuItem>
                <MenuItem value="agriculture">Agriculture</MenuItem>
                <MenuItem value="healthcare">Healthcare</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Location</InputLabel>
              <Select
                value={filters.location}
                label="Location"
                onChange={(e) => handleFilterChange('location', e.target.value)}
              >
                <MenuItem value="all">All Districts</MenuItem>
                <MenuItem value="Andhra Pradesh">Andhra Pradesh</MenuItem>
                <MenuItem value="Telangana">Telangana</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={1}>
            <Button
              fullWidth
              variant="contained"
              onClick={applyFilters}
              sx={{
                bgcolor: '#ff9800',
                '&:hover': { bgcolor: '#f57c00' },
                height: '40px'
              }}
            >
              Filter
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Schemes List */}
      {filteredSchemes.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No schemes found matching your criteria
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {paginatedSchemes.map((scheme) => (
            <Card key={scheme.id} sx={{ p: 3, '&:hover': { boxShadow: 6 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {scheme.title}
                    </Typography>
                    {scheme.is_eligible !== false && (
                      <Chip 
                        label="Eligible" 
                        size="small"
                        sx={{ 
                          bgcolor: '#e8f5e9', 
                          color: '#2e7d32',
                          fontWeight: 600
                        }} 
                      />
                    )}
                  </Box>
                  <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 700, mb: 1 }}>
                    ₹{scheme.subsidy_amount} Lakhs
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {scheme.short_description || scheme.description?.substring(0, 150) + '...'}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={() => handleViewDetails(scheme)}
                  sx={{
                    bgcolor: '#1976d2',
                    '&:hover': { bgcolor: '#1565c0' },
                    ml: 2,
                    minWidth: '140px'
                  }}
                >
                  View Details
                </Button>
              </Box>
            </Card>
          ))}
        </Box>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination 
              count={totalPages} 
              page={currentPage} 
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </>
      )}
    </Container>
  );

  // Render Scheme Details View
  const renderSchemeDetails = () => {
    if (!selectedScheme) return null;

    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 3 }}>
          <Link
            component="button"
            variant="body2"
            onClick={handleBackToList}
            sx={{ textDecoration: 'none', color: '#1976d2', cursor: 'pointer' }}
          >
            Home
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={handleBackToList}
            sx={{ textDecoration: 'none', color: '#1976d2', cursor: 'pointer' }}
          >
            Schemes & Subsidies
          </Link>
          <Typography color="text.primary">{selectedScheme.title}</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
            {selectedScheme.title}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {selectedScheme.short_description || selectedScheme.description}
          </Typography>

          {/* Tab Navigation */}
          <Tabs 
            value={activeDetailTab} 
            onChange={(e, newValue) => setActiveDetailTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="About" />
            <Tab label="Eligibility" />
            <Tab label="Benefits" />
            <Tab label="How to Apply" />
            <Tab label="Documents Required" />
          </Tabs>
        </Box>

        {/* About Tab - Complete Information */}
        {activeDetailTab === 0 && (
          <>
            {/* Key Info Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2.5, textAlign: 'center' }}>
                  <Factory sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Eligible Industries
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {capitalizeWords(selectedScheme.industry_type)}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2.5, textAlign: 'center' }}>
                  <AttachMoney sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Subsidy Limit
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedScheme.subsidy_limit_text || `Up to ₹ ${selectedScheme.subsidy_amount} Lakhs`}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2.5, textAlign: 'center' }}>
                  <CalendarToday sx={{ fontSize: 40, color: '#ff9800', mb: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Policy Validity
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedScheme.policy_validity || 
                      (selectedScheme.validity_start && selectedScheme.validity_end ? 
                        `${new Date(selectedScheme.validity_start).getFullYear()} - ${new Date(selectedScheme.validity_end).getFullYear()}` : 
                        `${new Date().getFullYear()} - ${new Date().getFullYear() + 5}`)}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2.5, textAlign: 'center' }}>
                  <LocationOn sx={{ fontSize: 40, color: '#f44336', mb: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Region
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedScheme.region || selectedScheme.location}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Overview Section */}
            <Paper sx={{ p: 4, mb: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                Overview
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  lineHeight: 1.8, 
                  color: '#555',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  textAlign: 'justify'
                }}
              >
                {selectedScheme.description}
              </Typography>
            </Paper>

            {/* Eligibility Section in About */}
            <Paper sx={{ p: 4, mb: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                Eligibility
              </Typography>
              {selectedScheme.eligibility_points && selectedScheme.eligibility_points.length > 0 ? (
                <List>
                  {selectedScheme.eligibility_points.map((point, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: '#4caf50' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={point}
                        primaryTypographyProps={{
                          sx: { lineHeight: 1.6 }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    lineHeight: 1.8, 
                    color: '#555',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    textAlign: 'justify'
                  }}
                >
                  {selectedScheme.eligibility}
                </Typography>
              )}
            </Paper>

            {/* Benefits Section in About */}
            <Paper sx={{ p: 4, mb: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                Benefits
              </Typography>
              {selectedScheme.benefits_table && selectedScheme.benefits_table.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedScheme.benefits_table.map((benefit, index) => (
                        <TableRow key={index}>
                          <TableCell>{benefit.type}</TableCell>
                          <TableCell>{benefit.description}</TableCell>
                          <TableCell>{benefit.value}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    lineHeight: 1.8, 
                    color: '#555',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    textAlign: 'justify'
                  }}
                >
                  {selectedScheme.benefits}
                </Typography>
              )}
            </Paper>

            {/* How to Apply Section in About */}
            <Paper sx={{ p: 4, mb: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                How to Apply
              </Typography>
              {selectedScheme.how_to_apply && selectedScheme.how_to_apply.length > 0 ? (
                <Stepper orientation="vertical">
                  {selectedScheme.how_to_apply.map((step, index) => (
                    <Step key={index} active>
                      <StepLabel>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {step.title || `Step ${index + 1}`}
                        </Typography>
                      </StepLabel>
                      <Typography variant="body2" sx={{ mt: 1, ml: 4, color: '#666' }}>
                        {step.description || step}
                      </Typography>
                    </Step>
                  ))}
                </Stepper>
              ) : (
                <Typography variant="body1" sx={{ lineHeight: 1.8, color: '#555' }}>
                  Please contact our consultants for application guidance.
                </Typography>
              )}
            </Paper>

            {/* Documents Required Section in About */}
            {selectedScheme.documents_required && selectedScheme.documents_required.length > 0 && (
              <Paper sx={{ p: 4, mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                  Documents Required
                </Typography>
                <List>
                  {selectedScheme.documents_required.map((doc, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Description sx={{ color: '#1976d2' }} />
                      </ListItemIcon>
                      <ListItemText primary={doc} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </>
        )}

        {/* Eligibility Section */}
        {activeDetailTab === 1 && (
          <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
            Eligibility
          </Typography>
          {selectedScheme.eligibility_points && selectedScheme.eligibility_points.length > 0 ? (
            <List>
              {selectedScheme.eligibility_points.map((point, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: '#4caf50' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={point}
                    primaryTypographyProps={{
                      sx: { lineHeight: 1.6 }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography 
              variant="body1" 
              sx={{ 
                lineHeight: 1.8, 
                color: '#555',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                textAlign: 'justify'
              }}
            >
              {selectedScheme.eligibility}
            </Typography>
          )}
          </Paper>
        )}

        {/* Benefits Section */}
        {activeDetailTab === 2 && (
          <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
            Benefits
          </Typography>
          {selectedScheme.benefits_table && selectedScheme.benefits_table.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedScheme.benefits_table.map((benefit, index) => (
                    <TableRow key={index}>
                      <TableCell>{benefit.type}</TableCell>
                      <TableCell>{benefit.description}</TableCell>
                      <TableCell>{benefit.value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography 
              variant="body1" 
              sx={{ 
                lineHeight: 1.8, 
                color: '#555',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                textAlign: 'justify'
              }}
            >
              {selectedScheme.benefits}
            </Typography>
          )}
          </Paper>
        )}

        {/* How to Apply Section */}
        {activeDetailTab === 3 && (
          <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
            How to Apply
          </Typography>
          {selectedScheme.how_to_apply && selectedScheme.how_to_apply.length > 0 ? (
            <Stepper orientation="vertical">
              {selectedScheme.how_to_apply.map((step, index) => (
                <Step key={index} active>
                  <StepLabel>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {step.title || `Step ${index + 1}`}
                    </Typography>
                  </StepLabel>
                  <Typography variant="body2" sx={{ mt: 1, ml: 4, color: '#666' }}>
                    {step.description || step}
                  </Typography>
                </Step>
              ))}
            </Stepper>
          ) : (
            <Typography variant="body1" sx={{ lineHeight: 1.8, color: '#555' }}>
              Please contact our consultants for application guidance.
            </Typography>
          )}
          </Paper>
        )}

        {/* Documents Required Section */}
        {activeDetailTab === 4 && selectedScheme.documents_required && selectedScheme.documents_required.length > 0 && (
          <Paper sx={{ p: 4, mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              Documents Required
            </Typography>
            <List>
              {selectedScheme.documents_required.map((doc, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <Description sx={{ color: '#1976d2' }} />
                  </ListItemIcon>
                  <ListItemText primary={doc} />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {/* Need Help Section */}
        <Paper sx={{ p: 4, bgcolor: '#f8f9fa', textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Need Help?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Connect with our ex-subsidiary consultant
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleRequestConsultation}
            sx={{
              bgcolor: '#1976d2',
              '&:hover': { bgcolor: '#1565c0' },
              px: 4,
              py: 1.5,
              fontWeight: 600
            }}
          >
            Request Consultation
          </Button>
        </Paper>
      </Container>
    );
  };

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ bgcolor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <Toolbar sx={{ px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
            <img src={logo} alt="TICSPL" style={{ height: '32px', marginRight: '12px' }} />
            <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600 }}>
              TICSPL
            </Typography>
          </Box>
          
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{ 
              flexGrow: 1,
              '& .MuiTab-root': {
                color: '#666',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '16px'
              },
              '& .Mui-selected': {
                color: '#1976d2 !important'
              }
            }}
          >
            <Tab label="Dashboard" />
            <Tab label="Projects" />
            <Tab label="Messages" />
            <Tab label="Schemes" />
          </Tabs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton sx={{ color: '#666' }}>
              <NotificationsOutlined />
            </IconButton>
            <Button
              color="inherit"
              onClick={handleMenuOpen}
              sx={{ 
                color: '#333',
                textTransform: 'none',
                fontWeight: 500
              }}
              endIcon={<Avatar sx={{ width: 32, height: 32, bgcolor: '#1976d2' }}>{user?.username?.charAt(0).toUpperCase()}</Avatar>}
            >
              {capitalizeUsername(user?.username) || 'User'}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItemComponent onClick={handleMenuClose}>Profile</MenuItemComponent>
              <MenuItemComponent onClick={handleLogout}>Logout</MenuItemComponent>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      {viewMode === 'list' ? renderSchemesList() : renderSchemeDetails()}

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default SchemesPage;
